import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { listOutwardGrns } from '@/api/outwardGrn';
import { createInvoiceFromGrn } from '@/api/salesInvoice';
import { getSettings } from '@/api/admin';
import { apiError } from '@/lib/apiError';
import { useFieldErrors } from '@/lib/useFieldErrors';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateInvoiceModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'grnId'>();
  const [grnId, setGrnId] = useState<number | null>(null);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [freightCharges, setFreightCharges] = useState('');
  const [irn, setIrn] = useState('');
  const [ewayBillNo, setEwayBillNo] = useState('');
  const [lrNo, setLrNo] = useState('');
  const [termsOfDelivery, setTermsOfDelivery] = useState('');
  const [bankName, setBankName]       = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankIfsc, setBankIfsc]       = useState('');

  const grnsQuery = useQuery({
    queryKey: ['outward-grn', 'invoiceable'],
    queryFn: () => listOutwardGrns({ status: 'Issued' }),
    enabled: open,
  });

  // Pull the company's saved bank details from Settings so the invoice
  // fields auto-fill instead of forcing re-entry every time.
  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
    enabled: open,
    staleTime: 60_000,
  });
  // Show only GRNs that don't already have an invoice attached.
  const eligible = useMemo(
    () =>
      (grnsQuery.data ?? []).filter(
        (g) => !g.salesInvoices || g.salesInvoices.length === 0,
      ),
    [grnsQuery.data],
  );

  const selected = useMemo(
    () => eligible.find((g) => g.id === grnId) ?? null,
    [eligible, grnId],
  );

  useEffect(() => {
    if (open) {
      setGrnId(null);
      setPaymentTerms('');
      setFreightCharges('');
      setIrn('');
      setEwayBillNo('');
      setLrNo('');
      setTermsOfDelivery('');
      setBankName('');
      setBankAccount('');
      setBankIfsc('');
    }
  }, [open]);

  // Auto-fill bank fields from company Settings once the query resolves.
  // Only pre-fills while the field is still empty — never overwrites text
  // the user has already typed.
  useEffect(() => {
    if (!open) return;
    const s = settingsQuery.data;
    if (!s) return;
    setBankName((v) => (v ? v : s.bankName ?? ''));
    setBankAccount((v) => (v ? v : s.bankAccount ?? ''));
    setBankIfsc((v) => (v ? v : s.bankIfsc ?? ''));
  }, [open, settingsQuery.data]);

  useEffect(() => {
    if (selected?.ewayBillNo) setEwayBillNo(selected.ewayBillNo);
    if (selected?.lrNo) setLrNo(selected.lrNo);
  }, [selected]);

  const createMut = useMutation({
    mutationFn: createInvoiceFromGrn,
    onSuccess: (r) => {
      toast.push(`Invoice ${r.invoiceNo} drafted (₹${r.grandTotal})`, 'success');
      qc.invalidateQueries({ queryKey: ['sales-invoices'] });
      qc.invalidateQueries({ queryKey: ['outward-grn'] });
      onClose();
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  function submit() {
    if (!fe.validate({ grnId: !grnId && 'Select an Outward GRN first' })) return;
    createMut.mutate({
      outwardGrnId: grnId!,
      freightCharges: freightCharges ? Number(freightCharges) : undefined,
      paymentTerms: paymentTerms || undefined,
      irn: irn || undefined,
      ewayBillNo: ewayBillNo || undefined,
      lrNo: lrNo || undefined,
      termsOfDelivery: termsOfDelivery || undefined,
      bankDetails: [bankName, bankAccount, bankIfsc].some(Boolean)
        ? `Bank Name: ${bankName}\nAccount No: ${bankAccount}\nIFSC Code: ${bankIfsc}`
        : undefined,
    });
  }

  return (
    <Modal
      open={open}
      title="Generate Invoice from Outward GRN"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={createMut.isPending || !grnId}
          >
            {createMut.isPending ? 'Drafting…' : 'Draft Invoice'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-2 sm:gap-3">
        <FormGroup label="Outward GRN (eligible — issued + un-invoiced)" required error={fe.errors.grnId}>
          <Select
            invalid={fe.has('grnId')}
            value={grnId ?? ''}
            onChange={(e) => { setGrnId(e.target.value ? Number(e.target.value) : null); fe.clear('grnId'); }}
          >
            <option value="">— Select Outward GRN —</option>
            {eligible.map((g) => (
              <option key={g.id} value={g.id}>
                {g.grnNo} · {g.dispatchOrder?.doNo ?? '—'} · {g.party?.name ?? '—'} ·{' '}
                {g.boxCount} box(es)
              </option>
            ))}
          </Select>
        </FormGroup>

        {selected && (
          <div className="rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2 text-xs sm:p-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className="text-[10px] uppercase text-[var(--text-muted)]">Vehicle / LR</div>
                <div className="font-semibold">
                  {selected.vehicleNo ?? '—'} · {selected.lrNo ?? '—'}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--text-muted)]">Customer</div>
                <div className="font-semibold">{selected.party?.name ?? '—'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--text-muted)]">Items</div>
                <div className="font-semibold">{selected.items.length}</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 sm:gap-3">
          <FormGroup label="Payment Terms">
            <Input
              placeholder="e.g. 30 days net"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Freight Charges (₹)">
            <Input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder="e.g. 1500"
              value={freightCharges}
              onChange={(e) => setFreightCharges(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="IRN (optional — e-invoice)">
            <Input value={irn} onChange={(e) => setIrn(e.target.value)} />
          </FormGroup>
          <FormGroup label="E-way Bill #">
            <Input value={ewayBillNo} onChange={(e) => setEwayBillNo(e.target.value)} />
          </FormGroup>
          <FormGroup label="Bill of Lading/LR-RR No.">
            <Input
              placeholder="e.g. LR-4471"
              value={lrNo}
              onChange={(e) => setLrNo(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Terms of Delivery">
            <Input
              placeholder="e.g. Ex-Works / FOB / Door Delivery"
              value={termsOfDelivery}
              onChange={(e) => setTermsOfDelivery(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Bank Name">
            <Input
              placeholder="e.g. State Bank of India"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Account Number">
            <Input
              placeholder="e.g. 7894561230"
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="IFSC Code">
            <Input
              placeholder="e.g. SBIN0001452"
              value={bankIfsc}
              onChange={(e) => setBankIfsc(e.target.value)}
            />
          </FormGroup>
        </div>

        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[10px] text-amber-900 sm:text-[11px]">
          GST split (CGST+SGST vs IGST) auto-computes from your company state vs
          consignee state. Customer outstanding increments only on{' '}
          <strong>Issue</strong>, not on Draft.
        </div>
      </div>
    </Modal>
  );
}
